import { Injectable } from '@angular/core';
import estadosData from '../../../assets/json/estados.json';
import municipiosData from '../../../assets/json/municipios.json';

// Interfaces para tipagem baseadas na estrutura real
export interface Estado {
  codigo_uf: number;
  uf: string;
  nome: string;
  latitude: number;
  longitude: number;
  regiao: string;
}

export interface Municipio {
  codigo_ibge: number;
  nome: string;
  latitude: number;
  longitude: number;
  capital: number;
  codigo_uf: number;
  siafi_id: number;
  ddd: number;
  fuso_horario: string;
}

@Injectable({
  providedIn: 'root',
})
export class EstadosCidadesService {
  private estados: Estado[] = estadosData;
  private municipios: Municipio[] = municipiosData;

  constructor() {}

  // Métodos para Estados
  getEstados(): Estado[] {
    return this.estados.sort((a, b) => a.nome.localeCompare(b.nome));
  }

  getEstadoPorCodigo(codigo: number): Estado | undefined {
    return this.estados.find((estado) => estado.codigo_uf === codigo);
  }

  getEstadoPorSigla(uf: string): Estado | undefined {
    return this.estados.find(
      (estado) => estado.uf.toLowerCase() === uf.toLowerCase()
    );
  }

  getEstadoPorNome(nome: string): Estado | undefined {
    return this.estados.find(
      (estado) => estado.nome.toLowerCase() === nome.toLowerCase()
    );
  }

  getEstadosPorRegiao(regiao: string): Estado[] {
    return this.estados
      .filter((estado) => estado.regiao.toLowerCase() === regiao.toLowerCase())
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  // Métodos para Municípios
  getMunicipios(): Municipio[] {
    return this.municipios;
  }

  getMunicipiosPorEstadoCodigo(codigoUf: number): Municipio[] {
    return this.municipios
      .filter((municipio) => municipio.codigo_uf === codigoUf)
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  getMunicipiosPorEstadoSigla(uf: string): Municipio[] {
    const estado = this.getEstadoPorSigla(uf);
    return estado ? this.getMunicipiosPorEstadoCodigo(estado.codigo_uf) : [];
  }

  getMunicipiosPorEstadoNome(nomeEstado: string): Municipio[] {
    const estado = this.getEstadoPorNome(nomeEstado);
    return estado ? this.getMunicipiosPorEstadoCodigo(estado.codigo_uf) : [];
  }

  getMunicipioPorCodigoIBGE(codigoIbge: number): Municipio | undefined {
    return this.municipios.find(
      (municipio) => municipio.codigo_ibge === codigoIbge
    );
  }

  getMunicipioPorNome(nome: string, codigoUf?: number): Municipio | undefined {
    let municipiosFiltrados = this.municipios.filter(
      (municipio) => municipio.nome.toLowerCase() === nome.toLowerCase()
    );

    if (codigoUf) {
      municipiosFiltrados = municipiosFiltrados.filter(
        (municipio) => municipio.codigo_uf === codigoUf
      );
    }

    return municipiosFiltrados[0];
  }

  getCapitais(): Municipio[] {
    return this.municipios
      .filter((municipio) => municipio.capital === 1)
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  getMunicipiosPorDDD(ddd: number): Municipio[] {
    return this.municipios
      .filter((municipio) => municipio.ddd === ddd)
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  // Métodos de busca e filtro
  buscarMunicipios(termo: string, codigoUf?: number): Municipio[] {
    let municipiosFiltrados = this.municipios.filter((municipio) =>
      municipio.nome.toLowerCase().includes(termo.toLowerCase())
    );

    if (codigoUf) {
      municipiosFiltrados = municipiosFiltrados.filter(
        (municipio) => municipio.codigo_uf === codigoUf
      );
    }

    return municipiosFiltrados.sort((a, b) => a.nome.localeCompare(b.nome));
  }

  buscarEstados(termo: string): Estado[] {
    return this.estados
      .filter(
        (estado) =>
          estado.nome.toLowerCase().includes(termo.toLowerCase()) ||
          estado.uf.toLowerCase().includes(termo.toLowerCase())
      )
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  // Métodos utilitários
  getNomeCompletoEstado(codigoUf: number): string {
    const estado = this.getEstadoPorCodigo(codigoUf);
    return estado ? `${estado.nome} (${estado.uf})` : '';
  }

  getNomeCompletoMunicipio(codigoIbge: number): string {
    const municipio = this.getMunicipioPorCodigoIBGE(codigoIbge);
    if (!municipio) return '';

    const estado = this.getEstadoPorCodigo(municipio.codigo_uf);
    return estado ? `${municipio.nome} - ${estado.uf}` : municipio.nome;
  }

  // Método para mapear dados do backend para códigos
  mapearEstadoStringParaCodigo(estadoString: string): number | null {
    // Tenta primeiro por sigla, depois por nome
    let estado = this.getEstadoPorSigla(estadoString);
    if (!estado) {
      estado = this.getEstadoPorNome(estadoString);
    }
    return estado ? estado.codigo_uf : null;
  }

  mapearMunicipioStringParaCodigo(
    municipioString: string,
    codigoUf?: number
  ): number | null {
    const municipio = this.getMunicipioPorNome(municipioString, codigoUf);
    return municipio ? municipio.codigo_ibge : null;
  }

  // Método específico para resolver o problema do formulário
  resolverEnderecoBackend(dadosBackend: { estado: string; cidade: string }): {
    estadoCodigo: number | null;
    municipioCodigo: number | null;
  } {
    const estadoCodigo = this.mapearEstadoStringParaCodigo(dadosBackend.estado);
    const municipioCodigo = estadoCodigo
      ? this.mapearMunicipioStringParaCodigo(dadosBackend.cidade, estadoCodigo)
      : null;

    return { estadoCodigo, municipioCodigo };
  }

  // Validações
  validarEstado(codigoUf: number): boolean {
    return this.getEstadoPorCodigo(codigoUf) !== undefined;
  }

  validarMunicipio(codigoIbge: number, codigoUf?: number): boolean {
    const municipio = this.getMunicipioPorCodigoIBGE(codigoIbge);
    if (!municipio) return false;

    if (codigoUf) {
      return municipio.codigo_uf === codigoUf;
    }

    return true;
  }

  // Métodos para compatibilidade com o formulário existente
  // Adaptação para manter compatibilidade com o código existente que usa id_uf e id_cidade
  getUfOptions(): Array<{ id_uf: number; nome: string; sigla: string }> {
    return this.estados
      .map((estado) => ({
        id_uf: estado.codigo_uf,
        nome: estado.nome,
        sigla: estado.uf,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  getCidadeOptions(): Array<{
    id_cidade: number;
    nome: string;
    id_uf: number;
  }> {
    return this.municipios
      .map((municipio) => ({
        id_cidade: municipio.codigo_ibge,
        nome: municipio.nome,
        id_uf: municipio.codigo_uf,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }
}
